import { restClient0 } from 'myNet'
import { updateToken } from 'services/jwt'
import ResourceService from 'api/Resource'
import binaryStringToUint8Array from 'utils/binary'
import { featureFlags, isDevBuild } from 'config/feature-flags'
import { getPlanLicense } from 'utils/plan/tier'
import { DEFAULT_CHUNK_SIZE } from 'services/attachmentV2/format'
import { decodeToFile, peekIsChunked } from 'services/attachmentV2/downloadManager'
// guardUnloadDuringUpload 는 이름만 업로드다 — 본문에 업로드 전용 로직이 없어(uploadManager.js:473-483)
// 다운로드 이탈 경고에 그대로 재사용한다. rename 은 호출처·테스트가 이름을 참조하므로 후속 정리.
import { uploadChunked, guardUnloadDuringUpload } from 'services/attachmentV2/uploadManager'

// 신포맷 파싱이 이 코드로 실패하면 구포맷으로 재시도한다 (설계 §2.3 — 판별 충돌 대비).
// DECRYPT_FAILED·SIZE_MISMATCH·UNSAFE_CHUNK_SIZE 는 헤더가 유효한 신포맷 파일의 실제 손상이거나
// 비규격 청크 크기이므로 폴백하지 않는다 — 폴백하면 구포맷 복호가 실패해 사용자가 원인을 알 수 없다.
// BAD_SOURCE 를 포함하는 이유(PR #1784 리뷰 문의): peekIsChunked 는 `slice`·`arrayBuffer` 만
// 요구하는데 decodeStream 은 `stream()` 이 필요하다. 둘 사이 간극(slice 는 있고 stream 은 없는
// 구형 Blob 구현)에서 BAD_SOURCE 가 나며, 그 경우 FileReader 기반 구포맷 경로는 정상 동작하므로
// 폴백이 옳다 — "양쪽 다 실패"가 아니다.
const CHUNK_PARSE_FALLBACK_CODES = new Set(['NOT_CHUNKED', 'BAD_HEADER', 'BAD_SOURCE'])

// 신 경로 적격 하한 = 청크 1개 크기. 이보다 작은 파일은 구 경로가 크래시 없이 처리하므로
// (실측 크래시 구간은 50MB~) 검증된 경로를 그대로 두고, 청크가 2개 이상 생기는 파일만
// 멀티파트로 보낸다 — 위험한 쪽(파트 분할·재시도·complete)만 새 코드를 타게 하는 선택이다.
export const CHUNKED_UPLOAD_MIN_SIZE = DEFAULT_CHUNK_SIZE

// 파일당 상한. LEGACY(10MB)는 플랜 미적용 환경의 기본값이자 단건 presigned 경로의 백엔드
// 게이트(UploadUrlFileModel le=MAX_FILE_SIZE)와 같은 값이다. 멀티파트·저장 경로의 백엔드
// 게이트는 절대 상한(ABSOLUTE_MAX_FILE_SIZE 500MB, backend/api/model/resource.py:788) +
// 플랜 검사(_enforce_per_file_size_or_raise — 글로벌향+flag ON 시 tier 산식)로 이원화됐다.
// FE 실효 상한은 getAttachmentSizeLimit() 가 서버 산출값(license.max_file_size_bytes)을
// 따르므로 두 층이 어긋나지 않는다. CHUNKED(500MB)는 FE 자체 천장(청크 경로 검증 범위).
export const LEGACY_ATTACHMENT_SIZE_LIMIT = 10 * 1024 * 1024
export const CHUNKED_ATTACHMENT_SIZE_LIMIT = 500 * 1024 * 1024

/**
 * 실효 파일당 상한.
 *
 * **서버가 산출한 플랜 상한(license.max_file_size_bytes)이 정본이다** (요금제 P6).
 * 백엔드 get_attachment_limits 가 tier 산식(Basic 10MB / PRO·TRIAL 30MB + 특약 override)을
 * license 응답으로 내려주며, legacy 값(10MB) 이하이면 플랜 미적용 환경이므로 기존 동작
 * (dev 빌드만 500MB, 그 외 10MB)을 유지한다 — dev 대용량 실측 흐름 보존.
 *
 * **flag 를 함께 보는 이유 — 킬스위치가 절반만 되돌리면 안 되기 때문이다.** 이 값은 파일 선택
 * 게이트가 쓰고(records/helper.js addAttachment), 경로 선택은 shouldUseChunkedUpload 가 한다.
 * flag 만 끄고 상한을 남겨두면 "대용량 선택은 통과하는데 처리는 구 경로" 조합이 되어, 구 경로가
 * 파일 전체를 힙에 올리다 크래시한다 — 이 기능이 애초에 없애려던 그 크래시다.
 * 사고 대응으로 flag 를 내리는 순간 더 나빠지므로, 상한과 경로는 같은 조건으로 함께 되돌린다.
 */
export const getAttachmentSizeLimit = () => {
  if (!featureFlags.chunkedAttachmentUpload) return LEGACY_ATTACHMENT_SIZE_LIMIT
  const serverLimit = getPlanLicense()?.max_file_size_bytes
  if (serverLimit > LEGACY_ATTACHMENT_SIZE_LIMIT) {
    // 프론트 자체 천장(500MB — 청크 경로 검증 범위) 초과분은 잘라낸다
    return Math.min(serverLimit, CHUNKED_ATTACHMENT_SIZE_LIMIT)
  }
  return isDevBuild ? CHUNKED_ATTACHMENT_SIZE_LIMIT : LEGACY_ATTACHMENT_SIZE_LIMIT
}

/**
 * 신 경로 적격 판정.
 *
 * flag 는 "코드 경로가 켜져 있다"는 뜻일 뿐이고, 실효 상한이 기존 10MB 를 넘지 않으면
 * 신 경로를 쓰지 않는다. 상한이 그대로인데 포맷만 바꾸면 이득 없이 신포맷 객체만 생기고,
 * 상한 근처 파일은 SBC1 오버헤드(헤더 36 + 청크당 28) 때문에 저장에서 588 로 거절된다.
 *
 * **usePresignedUpload 도 함께 본다 (PR #1792 Claude 리뷰)** — 청크 경로는 presigned
 * 멀티파트 API 로만 올라가고, 제출부의 청크 업로드 호출이 그 flag 블록 안에 있다
 * (RecordEdit.handleSubmit). 여기서 그 flag 를 보지 않으면 `usePresignedUpload=false` +
 * 청크 활성 조합에서 첨부시점 암호화는 건너뛰었는데 업로드가 호출되지 않아 **파일이 조용히
 * 유실**된다(FormData 경로는 chunkedFiles 를 모른다). 현재 두 flag 가 모두 상시 true 라
 * 도달 불가지만, 의존을 암묵적으로 두면 flag 를 내리는 순간 조용히 깨진다.
 */
export const shouldUseChunkedUpload = (attachment) =>
  featureFlags.chunkedAttachmentUpload === true &&
  featureFlags.usePresignedUpload === true &&
  getAttachmentSizeLimit() > LEGACY_ATTACHMENT_SIZE_LIMIT &&
  attachment?.originFile != null &&
  typeof attachment.originFile.size === 'number' &&
  attachment.originFile.size > CHUNKED_UPLOAD_MIN_SIZE

// 무진행(stall) 창. uploadManager 의 PART_STALL_TIMEOUT_MS(=60_000, uploadManager.js:38) 와 같은
// 값이지만 **export 하지 않고 여기 지역 선언**한다 — 그 모듈에 새 export 를 추가하면 그것을
// jest.mock 하는 기존 테스트의 factory 를 전부 보강해야 한다.
const DOWNLOAD_STALL_TIMEOUT_MS = 30_000

const downloadError = (code, message) => {
  const err = new Error(message)
  err.code = code
  return err
}

/**
 * 무진행 감지 타이머. 진행 신호마다 `arm()` 으로 재무장하고, 창을 넘기면 `onStall()` 을 부른다.
 *
 * 대용량 다운로드에 **총 시간 상한을 걸면 정상 다운로드가 잘리므로** 총량이 아니라 "정지"를 잰다.
 * 선례: uploadManager.js:58-92 putPart 의 armStallTimer.
 */
const createStallGuard = (onStall, stallMs, onWarn) => {
  let timer = null
  let warnTimer = null
  let warned = false
  // 경고 해제는 **여기 한 곳**에서 낸다 — 재개(arm 이 먼저 부른다)와 종료(settle·finally)가
  // 모두 cancel 을 지나므로 라이프사이클이 대칭이 된다. 종료 쪽을 컴포넌트의 IDLE_PROGRESS
  // 리셋에만 맡기면, progress 이벤트가 0건인 정지(서버가 아무것도 안 보내는 경우)에서
  // `percent` 가 -1 이라 `resetProgressIfIncomplete` 의 게이트(index.js:156)를 통과하지 못해
  // 행이 영구히 "연결 확인 중…" 으로 남는다 (PR #1853 리뷰 지적).
  // 경고를 낸 적이 있을 때만 통보한다 — `cancel()` 은 성공 경로와 progress 이벤트마다
  // (수백 회) 불리므로, 무조건 내면 불필요한 setState 가 쏟아진다.
  const cancel = () => {
    if (timer !== null) clearTimeout(timer)
    if (warnTimer !== null) clearTimeout(warnTimer)
    timer = null
    warnTimer = null
    if (warned) {
      warned = false
      if (onWarn != null) onWarn(false)
    }
  }
  const arm = () => {
    cancel()
    timer = setTimeout(onStall, stallMs)
    if (onWarn != null) {
      // 경고는 **창의 절반**. 별도 상수를 두지 않아 창을 바꾸면 경고도 함께 움직이고,
      // 창을 주입하는 기존 테스트(`{ stallMs: 1000 }`)에서도 경고가 정지보다 먼저 온다.
      // ⚠️ 이 콜백은 정지 타이머를 **건드리지 않는다** — 재무장하면 정지 판정이 영구히
      // 오지 않는다(silent 실패라 테스트가 유일한 방어선).
      warnTimer = setTimeout(() => {
        warned = true
        onWarn(true)
      }, stallMs / 2)
    }
  }
  return { arm, cancel }
}

/**
 * XHR 로 Blob 을 받는다 — presigned(S3 직행) 경로용.
 *
 * `fetch` 를 쓰지 않는 이유: 진행률을 얻으려면 리더 루프로 직접 읽어야 하고, 조각을 모으는 순간
 * 평문 전체가 JS 힙에 상주해 이 기능이 없애려던 OOM 이 되살아난다. XHR 은 `responseType='blob'`
 * 으로 브라우저 관리 저장소에 받고, progress 이벤트·`abort()` 를 플랫폼이 제공한다.
 * `restClient0.xhrGet` 은 `self.apiURL` 을 붙이므로(services/restClient/index.js:450) 외부 S3 URL
 * 에는 쓸 수 없어 지역 헬퍼가 필요하다.
 */
const xhrGetBlob = (url, options = {}) => {
  const { onProgress, stallMs = DOWNLOAD_STALL_TIMEOUT_MS, onXhr, onWarn } = options

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let settled = false
    let guard = null

    const settle = (fn, value) => {
      if (settled) return
      settled = true
      if (guard != null) guard.cancel()
      fn(value)
    }

    guard = createStallGuard(
      () => {
        // ⚠️ settle 을 abort 보다 **먼저** — abort 가 앞서면 abort 리스너가 ABORTED 로 선점해
        // 정지 원인이 취소로 가려진다 (uploadManager.js:81-84 와 같은 이유)
        settle(reject, downloadError('DOWNLOAD_STALLED', 'no download progress'))
        xhr.abort()
      },
      stallMs,
      onWarn,
    )

    xhr.responseType = 'blob'
    xhr.addEventListener('progress', (e) => {
      // 재무장은 **항상** 한다 — 진행률 보고 게이트(reportProgress)와 분리돼야 배경 다운로드가
      // 재무장을 못 해 창마다 죽는 일이 없다
      guard.arm()
      if (onProgress != null) onProgress(e)
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        settle(resolve, xhr.response)
      } else {
        settle(reject, downloadError('HTTP_ERROR', `HTTP ${xhr.status}`))
      }
    })
    xhr.addEventListener('error', () =>
      settle(reject, downloadError('NETWORK_ERROR', 'download failed')),
    )
    xhr.addEventListener('abort', () =>
      settle(reject, downloadError('ABORTED', 'download cancelled')),
    )

    // 호출부가 취소용으로 붙잡을 수 있게 먼저 넘긴다
    if (onXhr != null) onXhr(xhr)

    xhr.open('GET', url)
    guard.arm()
    xhr.send()
  })
}

/**
 * XHR progress 이벤트를 진행률 페이로드로 변환한다.
 *
 * `lengthComputable === false` / `total === 0` 가드가 필수다 — 없으면 restClient 의 기존 계산
 * `(e.loaded*100)/e.total`(services/restClient/index.js:418)처럼 NaN 이 된다. gzip 응답에서도
 * loaded/total 이 어긋나므로 같은 가드가 흡수한다.
 */
const toNetProgress = (evt) => {
  const loaded = evt != null && typeof evt.loaded === 'number' ? evt.loaded : 0
  const hasTotal =
    evt != null && evt.lengthComputable === true && typeof evt.total === 'number' && evt.total > 0
  const payload = { phase: 'net', bytes: loaded, totalBytes: hasTotal ? evt.total : null }
  if (hasTotal) {
    payload.ratio = loaded / evt.total
  }
  return payload
}

/**
 * uploadChunked 결과를 리소스 저장 body 의 파일 dict 로 변환.
 *
 * size 는 **암호문(S3 객체) 크기**여야 한다 — 백엔드가 실측 content_length 와 대조해 다르면
 * SAFEBOX_853 을 던진다. uploadChunked 의 size 는 평문이라 object_size 를 쓴다.
 * filename 은 mapUploadResultsToSendData 의 매칭 키이므로 uploadChunked 인자와 **같은 출처**를
 * 쓴다 (표기가 갈리면 한쪽만 바뀔 때 매칭이 조용히 깨진다).
 */
const toUploadResult = (attachment, result) => ({
  filename: attachment.file.name,
  object_key: result.object_key,
  etag: result.etag,
  size: result.object_size,
})

const commonProps = (attachment, apis) => {
  const self = attachment

  attachment.xhr = null

  if (self.manager != null) {
    self.loading = false
    self.manager.addAttachment(attachment)
  }

  attachment.setLoading = async (loading, reactObj) => {
    if (self.manager != null) {
      self.loading = loading
      self.manager.notifyLoadingChange()
    }
    await reactObj.setLoading(loading)
  }

  attachment.download = async (onProgress, reactObj, options = {}) => {
    await attachment.setLoading(true, reactObj)

    if (attachment.originFile != null && attachment.originFile.blobId == null) {
      const ret = {}

      let blob = attachment.originFile
      const fileName = attachment.originFile.name
      const mimetype = attachment.originFile.type
      let downloadBlob = blob

      if (mimetype != null) {
        downloadBlob = new Blob([blob], { type: mimetype })
      }

      ret.downloadPath = window.URL.createObjectURL(downloadBlob)

      ret.saveFile = () => {
        console.log(' - downloadImg : ', fileName, ret.downloadPath)
        const link = document.createElement('a')
        link.href = ret.downloadPath
        link.download = fileName
        link.click()
        link.remove() // IE 미지원
      }

      if (fileName != null) {
        if (mimetype === 'message/rfc822' || mimetype.indexOf('text/') === 0) {
          ret.text = await blob.text()
        }
      }

      if (fileName != null || mimetype != null) {
        if (blob.type === 'text/xml' && fileName != null) {
          const lcFileName = fileName.toLowerCase()
          if (lcFileName.endsWith('.svg') === true) {
            blob = new Blob([blob], { type: 'image/svg+xml' })
          } else if (
            lcFileName.endsWith('.heic') === true ||
            lcFileName.endsWith('.heif') === true
          ) {
            const tmpFile = new File([blob], fileName, { type: mimetype || 'image/heic' })
            // createObjectURL에는 blob, file 모두 사용가능하므로, file을 blob 대신 전달.
            self.spinning = false
            if (onProgress != null) {
              onProgress(null, null)
            }
            const { default: heictojpeg } = await import('heictojpeg')
            const file = await heictojpeg({ file: tmpFile, quality: 0.9 })
            console.log(file)
            self.spinning = false
            if (onProgress != null) {
              onProgress(null, null)
            }
            ret.path = window.URL.createObjectURL(file)
            attachment.setLoading(false, reactObj)
            return ret
          } else if (mimetype != null) {
            blob = new Blob([blob], { type: mimetype })
          }
        } else if (mimetype != null) {
          blob = new Blob([blob], { type: mimetype })
        }

        ret.blob = blob
        ret.path = window.URL.createObjectURL(blob)
      }

      attachment.setLoading(false, reactObj)
      return ret
    }

    if (attachment.url != null) {
      const { name, type } = attachment.file

      // 배경 다운로드(캐시 채움·.eml 프리다운로드)는 사용자가 시작한 작업이 아니므로 행의
      // 진행률 바를 움직이면 안 된다. **재무장·수신 자체는 그대로 진행한다** — 게이트는 UI
      // 보고만 막는다(게이트를 진행 이벤트에 걸면 배경 다운로드가 창마다 정지 판정을 받는다).
      // 킬스위치. OFF 면 수신을 예전 방식(fetch + response.blob())으로 되돌리고 진행률·정지
      // 감지·이탈 경고·취소 배선을 모두 끈다. 실패 안내(토스트)는 flag 와 무관하게 유지한다 —
      // 그것은 이미 발생한 거부에 반응할 뿐이라 자체 실패 모드가 없다(feature-flags.ts 주석 참조).
      const guardActive = featureFlags.attachmentDownloadGuard === true
      const reportProgress = guardActive && options.reportProgress !== false
      const { stallMs } = options
      const notifyNetProgress = (evt) => {
        if (!reportProgress) return
        if (attachment.onDownloadProgress == null) return
        try {
          attachment.onDownloadProgress(toNetProgress(evt))
        } catch (e) {
          // 진행률 표시 실패가 다운로드를 막아서는 안 된다 (매니저 2종과 같은 방침)
        }
      }
      // 중간 상태 통보. **보고는 게이트 안**, 타이머 무장은 게이트 밖이라는 분리를 지킨다 —
      // 타이머를 게이트 안으로 옮기는 것이 P4-e 사고("stall 재무장 × reportProgress 게이트가
      // 배경 다운로드를 창마다 죽인다")의 재발 경로다. 배경 다운로드에서도 타이머는 돌고
      // 통보만 여기서 막힌다(setTimeout 1개의 낭비는 의도된 대가).
      const notifyStallWarn = (stalling) => {
        if (!reportProgress) return
        if (attachment.onDownloadStalling == null) return
        try {
          attachment.onDownloadStalling(stalling)
        } catch (e) {
          // 표시 실패가 다운로드를 막아서는 안 된다 (notifyNetProgress 와 동일 방침)
        }
      }
      const captureXhr = (xhr) => {
        // 여기서 대입해야 기존 cancelDownload(:434) 의 xhr 분기가 처음으로 실제 동작한다
        attachment.xhr = xhr
      }

      // 프록시 경로에서만 채워진다 (아래 !isPresigned 블록). presigned 는 지역 헬퍼가 자체 처리.
      let proxyProgressHandler = null
      const progressBoundXhrs = new WeakSet()
      const onBeforeSendNew = (xhr) => {
        xhr.responseType = 'blob'
        if (!guardActive) return
        captureXhr(xhr)
        if (proxyProgressHandler == null) return
        // 401 재시도는 **같은 xhr 로** startReq 를 다시 부르므로(restClient/index.js:434-441)
        // 가드 없이 붙이면 리스너가 중복돼 진행률이 두 번씩 보고된다
        if (progressBoundXhrs.has(xhr)) return
        progressBoundXhrs.add(xhr)
        xhr.addEventListener('progress', proxyProgressHandler)
      }

      /**
       * 프록시 경로(우리 API 경유) 수신.
       *
       * 함수로 뽑은 이유: **401 재시도도 같은 보호를 받아야 한다.** 재시도는 decode 실패 분기에
       * 있어 위 수신 블록 밖이고, 정지 가드가 없으면 그 재시도가 매달릴 때 프라미스가 영구
       * pending 이 되어 "진행률이 멈춘 채 아무 일도 없는" 원래 증상이 재현된다.
       * `xhrGet` 의 onProgress 인자를 쓰지 않고 **onBeforeSend 로 넘어온 xhr 에 직접** 리스너를
       * 붙인다(그 콜백은 send() 직전에 호출된다 — restClient/index.js:472-476). 호출 인자 모양을
       * 바꾸지 않으므로 그 시그니처를 단언하는 기존 테스트가 유지된다.
       */
      const proxyGetBlob = async (url) => {
        if (!guardActive) {
          // OFF: 예전 호출 그대로 (진행률·정지 감지 없음)
          const { xhr } = await restClient0.xhrGet(url, null, null, onBeforeSendNew)
          return xhr.response
        }
        let stalled = false
        // ⚠️ 정지 시 **abort 에 의존해 정착하지 않는다.** xhr 핸들이 없거나 abort 가 reject 로
        // 이어지지 않으면 프라미스가 영구 pending 이 되어, 정지 가드를 붙인 목적("진행률이 멈춘 채
        // 아무 일도 없음")이 그대로 재현된다. putPart 가 settle 을 abort 보다 먼저 하는 것과 같은
        // 이유로(uploadManager.js:81-84) 자체적으로 거부되는 프라미스를 race 에 넣는다.
        let rejectOnStall = null
        const stallSignal = new Promise((_, reject) => {
          rejectOnStall = reject
        })
        const guard = createStallGuard(
          () => {
            stalled = true
            rejectOnStall(downloadError('DOWNLOAD_STALLED', 'no download progress'))
            // 연결 정리는 best effort — 실패해도 위에서 이미 정착했다
            if (attachment.xhr != null) attachment.xhr.abort()
          },
          stallMs != null ? stallMs : DOWNLOAD_STALL_TIMEOUT_MS,
          notifyStallWarn,
        )
        proxyProgressHandler = (evt) => {
          guard.arm()
          notifyNetProgress(evt)
        }

        try {
          guard.arm()
          const { xhr } = await Promise.race([
            restClient0.xhrGet(url, null, null, onBeforeSendNew),
            stallSignal,
          ])
          return xhr.response
        } catch (e) {
          // xhrGet 은 abort 를 **이벤트 객체 그대로** reject 한다(restClient/index.js:448) —
          // code 가 없으면 컴포넌트가 취소를 일반 실패로 오인해 잘못된 문구를 띄운다.
          if (stalled) throw downloadError('DOWNLOAD_STALLED', 'no download progress')
          if (e != null && e.type === 'abort') {
            throw downloadError('ABORTED', 'download cancelled')
          }
          throw e
        } finally {
          guard.cancel()
        }
      }

      let blob
      let isPresigned = false

      const filePathParts = attachment.url?.split('/file/')
      const objectKey = filePathParts?.length > 1 ? filePathParts[1] : null

      // 대용량 수신은 수 분이 걸릴 수 있어 이탈 경고를 붙인다(재개는 지원하지 않으므로 경고만).
      // 배경 다운로드(캐시 채움)까지 경고를 걸면 사용자가 시작하지도 않은 작업 때문에 탭을 못
      // 닫으므로 사용자 액션에서만 건다 — 그 판별자는 reportProgress 와 같다.
      const releaseUnloadGuard = reportProgress ? guardUnloadDuringUpload() : null

      try {
        if (objectKey && attachment.resourceData?.resource_id) {
          try {
            const purpose = options.extraParams?.includes('preview') ? 'preview' : 'download'

            const result = await ResourceService.getDownloadUrls(
              [{ resource_id: attachment.resourceData.resource_id, object_key: objectKey }],
              purpose,
            )

            const presignedFile = result.data?.files?.[0]
            if (presignedFile?.download_url) {
              if (guardActive) {
                blob = await xhrGetBlob(presignedFile.download_url, {
                  onProgress: notifyNetProgress,
                  onXhr: captureXhr,
                  stallMs,
                  onWarn: notifyStallWarn,
                })
              } else {
                // OFF: 예전 경로 그대로 (진행률·취소·정지 감지 없음)
                const response = await fetch(presignedFile.download_url)
                if (!response.ok) throw new Error(`HTTP ${response.status}`)
                blob = await response.blob()
              }
              isPresigned = true
            }
          } catch (err) {
            // 사용자 취소·정지는 **폴백 대상이 아니다** — 프록시로 재시도하면 취소가 무시되고
            // 정지 원인이 가려진다. presigned 발급 실패/네트워크 오류만 프록시로 강등한다.
            if (err?.code === 'ABORTED' || err?.code === 'DOWNLOAD_STALLED') {
              attachment.xhr = null
              attachment.setLoading(false, reactObj)
              throw err
            }
            console.error('Failed to get presigned URL, falling back to proxy', err)
          }
        }

        if (!isPresigned) {
          const sep = attachment.url.includes('?') ? '&' : '?'
          const downloadUrl = options.extraParams
            ? `${attachment.url}${sep}${options.extraParams}`
            : attachment.url
          blob = await proxyGetBlob(downloadUrl)
        }
      } finally {
        // 수신 구간이 끝나면(성공·실패·취소 무관) 이탈 경고를 해제한다. 남기면 이후 탭 닫기마다
        // 브라우저 경고가 뜬다.
        if (releaseUnloadGuard != null) releaseUnloadGuard()
      }

      // 네트워크 구간이 끝났으므로 취소 핸들을 비운다 — 남겨두면 다음 다운로드가 끝난 xhr 을
      // abort 하려 하고, 진행 중이 아닌데 취소 가능 상태로 보인다.
      attachment.xhr = null

      let downloadBlob = new File([blob], name, { type })

      // 복호 구간 취소용. decodeStream 이 signal.aborted 를 읽으므로(downloadManager.js:105)
      // 이 구간에는 네트워크와 별개로 AbortController 가 필요하다. 업로드의
      // abortChunkedUpload(:648 계열)와 같은 "helper 가 만들고 finally 에서 비운다" 구조다.
      // flag OFF 면 만들지 않는다 — 취소 배선이 남으면 "OFF = 예전 동작" 이 성립하지 않는다.
      const decryptController =
        guardActive && typeof AbortController === 'function' ? new AbortController() : null
      attachment.abortDecrypt = decryptController == null ? null : () => decryptController.abort()
      const decodeOptions = {
        signal: decryptController == null ? undefined : decryptController.signal,
        reportProgress,
      }

      try {
        downloadBlob = await attachment.decode(downloadBlob, null, decodeOptions)
      } catch (e) {
        if (e.code === 401) {
          // 재시도도 수 분짜리 수신이라 이탈 경고를 다시 건다(위 수신 구간의 것은 이미 해제됐다).
          const releaseRetryGuard = reportProgress ? guardUnloadDuringUpload() : null
          try {
            await updateToken()
            // proxyGetBlob 을 쓰므로 정지 가드·취소 코드 매핑이 재시도에도 적용된다
            blob = await proxyGetBlob(attachment.url)
            attachment.xhr = null
            downloadBlob = new File([blob], name, { type })
            downloadBlob = await attachment.decode(downloadBlob, null, decodeOptions)
          } catch (retryError) {
            console.error('decode error after token refresh - ', retryError)
            attachment.abortDecrypt = null
            attachment.setLoading(false, reactObj)
            throw retryError
          } finally {
            if (releaseRetryGuard != null) releaseRetryGuard()
          }
        } else {
          console.error('decode error - ', e)
          attachment.abortDecrypt = null
          attachment.setLoading(false, reactObj)
          throw e
        }
      }

      // 복호가 끝났으므로 취소 핸들을 비운다 (stale 방지 — R7)
      attachment.abortDecrypt = null

      const ret = {
        downloadPath: window.URL.createObjectURL(downloadBlob),
      }

      // arrayBuffer 는 **접근 시점에만** 만든다 (PR #1784 리뷰 지적).
      // 무조건 await 하면 평문 전체가 JS 힙 ArrayBuffer 로 올라와, downloadManager 가
      // 청크별 Blob 으로 상주 메모리를 O(청크)로 묶어둔 이득이 이 한 줄에서 상쇄된다
      // (500MB 신포맷 파일이면 힙 500MB 재현). 구포맷은 소형이라 드러나지 않던 문제다.
      // 현재 이 필드를 읽는 소비자는 없으나(src 전수 grep — attachment.download 결과에서
      // arrayBuffer 접근 0건, `...ret` 스프레드 0건) 계약 유지를 위해 제거하지 않고 지연화만 한다.
      // Promise 를 반환하므로 기존 `await ret.arrayBuffer` 사용법은 그대로 동작한다.
      Object.defineProperty(ret, 'arrayBuffer', {
        get: () => downloadBlob.arrayBuffer(),
        enumerable: true,
        configurable: true,
      })

      ret.saveFile = () => {
        console.log(' - downloadImg : ', name, ret.downloadPath)
        const link = document.createElement('a')
        link.href = ret.downloadPath
        link.download = name
        link.click()
        link.remove() // IE 미지원
      }

      if (name != null) {
        if (type === 'message/rfc822' || type.indexOf('text/') === 0) {
          ret.text = await blob.text()
        }
      }

      if (name != null || type != null) {
        if (blob.type === 'text/xml' && name != null) {
          const lcFileName = name.toLowerCase()
          if (lcFileName.endsWith('.svg') === true) {
            blob = new Blob([blob], { type: 'image/svg+xml' })
          } else if (
            lcFileName.endsWith('.heic') === true ||
            lcFileName.endsWith('.heif') === true
          ) {
            const tmpFile = new File([blob], name, { type: type || 'image/heic' })
            // createObjectURL에는 blob, file 모두 사용가능하므로, file을 blob 대신 전달.
            self.spinning = false
            if (onProgress != null) {
              onProgress(null, null)
            }
            const { default: heictojpeg } = await import('heictojpeg')
            const file = await heictojpeg({ file: tmpFile, quality: 0.9 })
            console.log(file)
            self.spinning = false
            if (onProgress != null) {
              onProgress(null, null)
            }
            ret.path = window.URL.createObjectURL(file)

            attachment.setLoading(false, reactObj)
            return ret
          } else if (type != null) {
            blob = new Blob([blob], { type })
          }
        } else if (type != null) {
          blob = new Blob([blob], { type })
        }

        ret.path = window.URL.createObjectURL(blob)
      }

      attachment.setLoading(false, reactObj)
      return ret
    }

    return null
  }

  /**
   * 진행 중인 다운로드를 끊는다.
   *
   * 이 함수는 이전부터 있었고 helperUI 의 팝업 닫기 5지점이 호출하고 있었지만, 프로덕션에서
   * `attachment.xhr` 에 실제 xhr 을 넣는 코드가 없어 **운영에서 no-op** 이었다(읽기 6지점 /
   * 쓰기 0지점). 이제 두 네트워크 경로가 captureXhr 로 대입하므로 실제로 동작한다.
   * 복호 구간은 XHR 이 아니라 AbortSignal 로 끊어야 해서 분기가 하나 더 필요하다.
   */
  attachment.cancelDownload = () => {
    if (attachment.xhr != null) {
      try {
        attachment.xhr.abort()
      } catch (err) {
        console.log(err)
      }
      attachment.xhr = null
    }
    if (attachment.abortDecrypt != null) {
      try {
        attachment.abortDecrypt()
      } catch (err) {
        console.log(err)
      }
      attachment.abortDecrypt = null
    }
  }

  attachment.delete = () => {
    if (apis.deleteAttachment != null) {
      apis.deleteAttachment(attachment)

      if (self.manager != null) {
        self.manager.notifyLengthChange()
      }
    }
  }

  attachment.compare = (other) => {
    let ret = false
    if (attachment === other) {
      ret = true
    } else if (
      attachment.file.blobId !== other.file.blobId ||
      attachment.file.name !== other.file.name ||
      attachment.file.size !== other.file.size ||
      attachment.file.type !== other.file.type
    ) {
      ret = false
    }
    return ret
  }

  attachment.encode = (onEnd) => {
    return new Promise((resolve) => {
      const { name, type } = attachment.file

      const reader = new FileReader()

      reader.addEventListener(
        'load',
        async () => {
          console.log(' -- buffer : ', reader.result)
          const encrypted = await attachment.rsKeyObj.encryptBytes(reader.result)
          console.log(' -- encrypted : ', encrypted.length, encrypted)
          const etag = attachment.rsKeyObj.md5(encrypted)
          console.log(' -- etag : ', etag)

          const uint8Array = binaryStringToUint8Array(encrypted)

          const encodedFile = new File([uint8Array], name, { type })

          attachment.encodedFile = encodedFile
          attachment.etag = etag

          if (onEnd != null) {
            onEnd({ encrypted, encodedFile })
          }

          resolve(attachment)
        },
        false,
      )

      reader.readAsArrayBuffer(attachment.originFile)
    })
  }

  // 신포맷(SBC1) 스트리밍 복호를 먼저 시도하고, 구포맷이면 아래 기존 경로로 내려간다.
  // 판별과 폴백 분기는 **helper 쪽 책임**이다 — attachmentV2 모듈은 helper 를 import 하지
  // 않으므로(설계 §5.1 순환 방지) 여기서만 갈라야 한다.
  attachment.decodeChunked = async (file, onEnd, options = {}) => {
    const { name, type } = attachment.file
    const reportDecryptProgress = options.reportProgress !== false
    // 복호 구간은 바에서 90→100 을 차지한다 — phase 를 붙여야 컴포넌트가 그 범위로 매핑한다.
    // 배경 다운로드에서는 게이트가 UI 보고만 막는다(복호 자체는 그대로 진행).
    const onDecryptProgress = (payload) => {
      if (!reportDecryptProgress) return
      if (attachment.onDecodeProgress == null) return
      attachment.onDecodeProgress({ ...payload, phase: 'decrypt' })
    }
    const decodedFile = await decodeToFile(file, attachment.rsKeyObj.rsKey, name, {
      type,
      onProgress: onDecryptProgress,
      signal: options.signal,
    })
    // 구포맷 경로는 onEnd({ decrypted, decodedFile }) 로 평문 바이너리 문자열까지 넘기지만
    // (아래 :383) 여기서는 **의도적으로 decodedFile 만** 넘긴다 — `decrypted` 는 평문 전체를
    // JS 문자열로 materialize 하는 것이라 대용량에서 정확히 이 PR 이 없애려는 힙 상주가 된다.
    // 현재 프로덕션 호출부는 onEnd 를 넘기지 않으므로(:147·:160) 실사용 차이는 없다.
    if (onEnd != null) {
      onEnd({ decodedFile })
    }
    return decodedFile
  }

  attachment.decode = async (file, onEnd, options = {}) => {
    // Blob 은 재판독 가능하므로 선두 4바이트 확인이 아래 경로의 입력을 소비하지 않는다
    if (await peekIsChunked(file)) {
      try {
        return await attachment.decodeChunked(file, onEnd, options)
      } catch (e) {
        if (!CHUNK_PARSE_FALLBACK_CODES.has(e?.code)) throw e
        // magic 만 우연히 일치한 구포맷 — 아래 기존 경로로 재시도
        console.warn('SBC1 magic matched but header invalid, falling back to legacy format')
      }
    }

    return new Promise((resolve, reject) => {
      const { name, type } = attachment.file

      const reader = new FileReader()

      reader.addEventListener(
        'load',
        async () => {
          // console.log(' -- buffer2 : ', reader.result);
          const bytes = new Uint8Array(reader.result)

          // 길이가 100byte 이하인 경우, 에러인지 확인. 에러 JSON 응답은 항상 소형이므로
          // 상한 조건(구: length > 10MB)은 제거 — 파일 상한이 오르면 전체 버퍼를 spread 해
          // RangeError(콜 스택 초과)가 나고, 하드코딩 10485760 은 상한 상수와 어긋난다.
          if (bytes.length < 100) {
            const jsonStr = String.fromCharCode(...new Uint8Array(reader.result))

            try {
              const jsonObj = JSON.parse(jsonStr)
              const { code, message } = jsonObj
              if (code != null && code !== 200) {
                console.error(' -- error : ', code, message)
                const error = new Error(message)
                error.code = code
                reject(error)
                return
              }
            } catch (e) {
              // 정상적인 상태면 parsing이 되지 않는다.
              console.log('success')
            }
          }

          let binaryStr = ''
          for (let i = 0; i < Math.ceil(bytes.length / 32768.0); i += 1) {
            binaryStr += String.fromCharCode.apply(
              null,
              bytes.slice(i * 32768, Math.min((i + 1) * 32768, bytes.length)),
            )
          }
          const decrypted = await attachment.rsKeyObj.decryptBytes(binaryStr)

          console.log(' -- decrypted : ', decrypted)

          const uint8Array = binaryStringToUint8Array(decrypted)
          const decodedFile = new File([uint8Array], name, { type })

          if (onEnd != null) {
            onEnd({ decrypted, decodedFile })
          }

          resolve(decodedFile)
        },
        false,
      )

      reader.readAsArrayBuffer(file)
    })
  }

  return self
}

const createAttachment = (originFile, rsKeyObj, apis, manager) => {
  const { name, size, type } = originFile
  const attachment = {
    file: {
      name,
      size,
      type: type != null && type.length > 0 ? type : 'application/unknown',
    },
    key: `${name}_${size}`,
    originFile,
    uploaded: false,
    rsKeyObj,
    manager,
  }

  attachment.startEncoding = async (reactObj) => {
    // 신 경로는 **첨부 시점에 암호화하지 않는다**. 구 경로의 encode 는 파일 전체를
    // ArrayBuffer → 암호문 문자열 → File 로 세 번 힙에 올려서(:296-308) 50MB 부터 크래시하므로,
    // 대용량은 제출 시점에 청크 단위로 암호화·업로드를 한 번에 흘려보낸다(uploadChunkedTo).
    // 여기서 loading 을 걸지 않는 이유: 실제 작업이 제출까지 미뤄지므로 걸면 영구 스피너가 된다.
    if (shouldUseChunkedUpload(attachment)) {
      attachment.deferredChunkedUpload = true
      return
    }

    await attachment.setLoading(true, reactObj)

    setTimeout(() => {
      attachment.encode(() => {
        attachment.setLoading(false, reactObj)
      })
    }, 50)
  }

  // 제출 시점 스트리밍 업로드. resourceId 는 호출부(RecordEdit)가 확정해 넘긴다 —
  // 신규 레코드는 저장 전이라 서버에 행이 없지만, initiate 는 object_key 의 워크스페이스·
  // resource_id 일치만 검증하고 행 존재를 요구하지 않는다(services/attachment_multipart_service.py:79-80).
  attachment.uploadChunkedTo = async (resourceId, options = {}) => {
    // 진행률·취소는 호출부(RecordEdit)가 아니라 여기서 배선한다 — RecordEdit 은 어느 파일이
    // 지금 올라가는 중인지 UI 에 알릴 경로가 없고, 진행률 콜백은 첨부 행 컴포넌트가 등록해 둔다
    // (Attachment/index.js componentDidMount 의 onUploadProgress).
    const controller = typeof AbortController === 'function' ? new AbortController() : null
    attachment.abortChunkedUpload = controller == null ? null : () => controller.abort()

    try {
      const result = await uploadChunked(
        attachment.originFile,
        {
          resourceId,
          password: attachment.rsKeyObj.rsKey,
          filename: attachment.file.name,
          // attachment.file.type 은 빈 MIME 을 'application/unknown' 으로 보정한 값이다(:421-425)
          contentType: attachment.file.type,
        },
        {
          onProgress: attachment.onUploadProgress,
          signal: controller == null ? undefined : controller.signal,
          // 호출부가 명시로 넘긴 값이 위 기본값을 이긴다 (테스트에서 fake worker·signal 주입)
          ...options,
        },
      )
      return toUploadResult(attachment, result)
    } finally {
      // 취소 핸들러가 남아 있으면 완료된 업로드를 취소하려는 클릭이 조용히 무시되는 대신
      // 이미 끝난 controller 를 건드린다 — 진행 중이 아닐 때는 취소 경로 자체를 닫는다.
      attachment.abortChunkedUpload = null
      // 실패·취소는 onProgress 를 더 호출하지 않아 진행률이 마지막 값(예: 40%)에 갇힌다.
      // 첨부 행의 진행률 오버레이는 그 값으로 계속 덮여 있으므로 종료를 따로 알려야 한다.
      if (typeof attachment.onUploadSettled === 'function') {
        attachment.onUploadSettled()
      }
    }
  }

  commonProps(attachment, apis)

  return attachment
}

const createAttachmentByResource = (resourceData, rsKeyObj, apis, manager) => {
  const { filename: name, size, content_type: type, id, url } = resourceData
  const attachment = {
    file: {
      name,
      size,
      type,
    },
    key: id,
    resourceData,
    uploaded: true,
    url,
    rsKeyObj,
    manager,
  }

  commonProps(attachment, apis)

  return attachment
}

function AttachmentManager() {
  const self = this

  self.attachmentList = []
  self.changeListeners = []

  self.addAttachment = (attachment) => {
    self.attachmentList.push(attachment)
    self.notifyLengthChange()
  }

  self.removeAttachment = (attachment) => {
    const pos = self.attachmentList.indexOf(attachment)
    if (pos >= 0) {
      self.attachmentList.splice(pos, 1)
      self.notifyLengthChange()
    }
  }

  self.notifyLoadingChange = () => {
    const isLoading = self.attachmentList.some((attachment) => {
      return attachment.loading === true
    })

    self.changeListeners.forEach((listenerObj) => {
      listenerObj.loadingChanged(isLoading)
    })
  }

  self.notifyLengthChange = () => {
    self.changeListeners.forEach((listenerObj) => {
      listenerObj.lengthChanged(self.attachmentList.length)
    })
  }

  self.addChangeListener = (listenerObj) => {
    self.changeListeners.push(listenerObj)
  }

  self.removeChangeListener = (listenerObj) => {
    const pos = self.changeListeners.indexOf(listenerObj)
    if (pos >= 0) {
      self.changeListeners.splice(pos, 1)
    }
  }
}

const createAttachmentManager = (listenerObj) => {
  const attachmentManager = new AttachmentManager()
  attachmentManager.addChangeListener(listenerObj)

  return attachmentManager
}

function isPreviewEnable(file) {
  console.log('isPreviewEnable - ', file)

  if (file != null && file.type != null) {
    const { type } = file

    const mimeType = type.toLowerCase()

    const types = mimeType.split('/')

    switch (types[0]) {
      case 'image':
      case 'video':
      case 'audio':
      case 'text':
        return true
      default:
    }

    switch (mimeType) {
      case 'message/rfc822':
      case 'text/plain':
      case 'text/xml':
        return true
      default:
    }
  }

  return false
}

export const DRAGDOCS_EXTENSIONS = new Set([
  'hwp',
  'hwpx',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
  'odt',
  'pdf',
])

const TEXT_EXTENSIONS = new Set(['md', 'csv', 'log', 'json', 'xml', 'yaml', 'yml', 'txt'])

function getFileExtension(fileName) {
  if (!fileName) return ''
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot < 0) return ''
  return fileName.substring(lastDot + 1).toLowerCase()
}

function isTextViewable(file) {
  if (!file || !file.name) return false
  return TEXT_EXTENSIONS.has(getFileExtension(file.name))
}

function isViewEnabled(file) {
  if (!file || !file.name) return false
  const ext = getFileExtension(file.name)
  return DRAGDOCS_EXTENSIONS.has(ext) || TEXT_EXTENSIONS.has(ext)
}

// 미리보기 크기 상한 (설계 §5.3 3단 정책). 미디어는 면제, 그 외는 이 값을 넘으면 미리보기 대신
// 다운로드만 허용한다. 첨부 상한(LEGACY/CHUNKED_ATTACHMENT_SIZE_LIMIT)과 별개 값인 이유:
// 저장 가능 크기와 "브라우저에서 열어도 괜찮은 크기"는 다른 판단이다.
export const PREVIEW_SIZE_LIMIT = 50 * 1024 * 1024

// 미디어는 전체 다운로드+복호 후 재생이라 시간은 걸리지만 뷰어가 스트리밍으로 소비하므로
// 크기를 제한하지 않는다(설계: "media = 무제한"). 진행률 표시는 P4-c 에서 붙었다.
const isPreviewMediaType = (file) => {
  const mimeType = file?.type?.toLowerCase() ?? ''
  const topLevel = mimeType.split('/')[0]
  return topLevel === 'image' || topLevel === 'video' || topLevel === 'audio'
}

/**
 * 크기 기준 미리보기 허용 판정.
 *
 * **기존 판정 함수(isPreviewEnable/isViewEnabled) 본문을 건드리지 않고 별도로 둔 이유**:
 * 두 함수는 기준이 다르다(전자는 MIME, 후자는 확장자). `.txt` 처럼 양쪽에 모두 걸리는 파일이
 * 있어서 각 함수 안에 크기 검사를 심으면 판정이 중복되고, 나중에 한쪽만 고치면 두 경로가
 * 어긋난다. 호출부에서 AND 하면 크기 정책이 한 곳에만 존재한다.
 *
 * size 를 모르는 경우는 **허용**한다 — 기존 동작(미리보기 가능)을 유지하는 쪽이 안전하고,
 * 크기 미지정은 대용량 신호가 아니다.
 */
function isPreviewSizeAllowed(file) {
  if (isPreviewMediaType(file)) return true
  const size = file?.size
  if (typeof size !== 'number' || !Number.isFinite(size)) return true
  return size <= PREVIEW_SIZE_LIMIT
}

function isNotDragDocsExtension(file) {
  if (!file || !file.name) return true
  const ext = getFileExtension(file.name)
  return !DRAGDOCS_EXTENSIONS.has(ext) && !TEXT_EXTENSIONS.has(ext)
}

const helper = {
  createAttachment,
  createAttachmentByResource,
  createAttachmentManager,
  isPreviewEnable,
  isTextViewable,
  isViewEnabled,
  isNotDragDocsExtension,
  isPreviewSizeAllowed,
  PREVIEW_SIZE_LIMIT,
}

export default helper
